import React from 'react'
import { motion } from 'framer-motion'
import { useInView } from 'react-intersection-observer'
import {Check, Sparkles, Crown, Zap} from 'lucide-react'

const Pricing = () => {
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  })

  const scrollToPricing = () => {
    window.scrollTo({ top: 0, behavior: 'smooth' })
  }

  const plans = [
    {
      name: 'Mensal',
      icon: Zap,
      price: '49,90',
      period: 'por mês',
      description: 'Acesso completo sem compromisso longo',
      features: [
        'Todos os cursos',
        'Professores reais',
        'Lucy IA 24h',
        'Comunidade',
        'Aulas ao vivo',
      ],
      popular: false,
      cta: 'Assinar plano mensal',
      gradient: 'from-gray-700 to-gray-900',
    },
    {
      name: 'Semestral',
      icon: Sparkles,
      price: '224,55',
      originalPrice: '299,40',
      period: 'à vista',
      discount: '25% OFF',
      description: 'Tempo ideal para criar constância e ver evolução real',
      features: [
        'Tudo do plano mensal',
        'Economia significativa',
        'Tempo ideal para resultados',
        'Suporte prioritário',
        'Garantia de 6 meses',
      ],
      popular: true,
      cta: 'Assinar plano semestral',
      gradient: 'from-[#0D7FA3] to-[#C64DAD]',
    },
    {
      name: 'Anual',
      icon: Crown,
      price: '404,19',
      originalPrice: '598,80',
      period: 'à vista',
      discount: '32% OFF',
      description: 'Melhor custo-benefício para consolidar o inglês',
      features: [
        'Tudo do plano mensal',
        'Maior economia',
        'Consolidação garantida',
        'Suporte VIP',
        'Bônus exclusivos',
      ],
      popular: false,
      cta: 'Assinar plano anual',
      gradient: 'from-yellow-600 to-orange-600',
    },
  ]

  return (
    <section id="pricing" ref={ref} className="py-20 md:py-32 bg-gradient-to-b from-white to-gray-50 relative overflow-hidden">
      {/* Decorative elements */}
      <div className="absolute inset-0 opacity-5">
        <div className="absolute top-20 left-0 w-96 h-96 bg-[#0D7FA3] rounded-full blur-3xl"></div>
        <div className="absolute bottom-20 right-0 w-96 h-96 bg-[#C64DAD] rounded-full blur-3xl"></div>
      </div>

      <div className="container mx-auto px-4 md:px-6 relative z-10">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ duration: 0.8 }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl lg:text-6xl font-black text-gray-900 mb-6 leading-tight">
              Escolha o plano{' '}
              <span className="bg-gradient-to-r from-[#0D7FA3] to-[#C64DAD] bg-clip-text text-transparent">
                ideal para você
              </span>
            </h2>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-8">
            {plans.map((plan, index) => {
              const Icon = plan.icon
              const isPopular = plan.popular
              
              return (
                <motion.div
                  key={index}
                  initial={{ opacity: 0, y: 50 }}
                  animate={inView ? { opacity: 1, y: 0 } : {}}
                  transition={{ duration: 0.6, delay: index * 0.2 }}
                  whileHover={{ y: -12, scale: isPopular ? 1.02 : 1.05 }}
                  className={`
                    relative bg-white rounded-3xl shadow-xl hover:shadow-2xl transition-all duration-300
                    ${isPopular ? 'ring-4 ring-[#0D7FA3] md:scale-105' : 'border-2 border-gray-200'}
                  `}
                >
                  {/* Popular badge */}
                  {isPopular && (
                    <div className="absolute -top-5 left-1/2 -translate-x-1/2 z-10">
                      <motion.div
                        animate={{ y: [0, -5, 0] }}
                        transition={{ duration: 2, repeat: Infinity }}
                        className="bg-gradient-to-r from-[#0D7FA3] to-[#C64DAD] text-white font-bold px-6 py-2 rounded-full shadow-lg flex items-center gap-2"
                      >
                        <Sparkles className="w-4 h-4" />
                        MAIS ESCOLHIDO
                      </motion.div>
                    </div>
                  )}

                  {/* Discount badge */}
                  {plan.discount && (
                    <div className="absolute top-6 right-6">
                      <div className="bg-yellow-300 text-gray-900 font-bold text-sm px-3 py-1 rounded-full">
                        {plan.discount}
                      </div>
                    </div>
                  )}

                  <div className="p-8">
                    {/* Icon */}
                    <div className={`inline-flex p-4 rounded-2xl bg-gradient-to-br ${plan.gradient} mb-4`}>
                      <Icon className="w-8 h-8 text-white" strokeWidth={2} />
                    </div>

                    {/* Plan name */}
                    <h3 className="text-2xl font-bold text-gray-900 mb-2">
                      {plan.name}
                    </h3>

                    {/* Description */}
                    <p className="text-gray-600 mb-6 min-h-[48px]">
                      {plan.description}
                    </p>

                    {/* Price */}
                    <div className="mb-6">
                      {plan.originalPrice && (
                        <p className="text-gray-400 line-through text-lg mb-1">
                          De R$ {plan.originalPrice}
                        </p>
                      )}
                      <div className="flex items-baseline gap-2">
                        <span className="text-gray-600 text-lg">R$</span>
                        <span className="text-5xl font-black text-gray-900">
                          {plan.price}
                        </span>
                      </div>
                      <p className="text-gray-600 mt-1">{plan.period}</p>
                    </div>

                    {/* Features */}
                    <div className="space-y-3 mb-8">
                      {plan.features.map((feature, i) => (
                        <div key={i} className="flex items-start gap-3">
                          <div className="w-5 h-5 bg-green-500 rounded-full flex items-center justify-center flex-shrink-0 mt-0.5">
                            <Check className="w-3 h-3 text-white" strokeWidth={3} />
                          </div>
                          <span className="text-gray-700">{feature}</span>
                        </div>
                      ))}
                    </div>

                    {/* CTA */}
                    <motion.button
                      onClick={scrollToPricing}
                      whileHover={{ scale: 1.02 }}
                      whileTap={{ scale: 0.98 }}
                      className={`
                        w-full py-4 rounded-xl font-bold text-lg transition-all duration-300 relative overflow-hidden group
                        ${isPopular 
                          ? 'bg-gradient-to-r from-[#0D7FA3] to-[#C64DAD] text-white shadow-lg hover:shadow-xl' 
                          : 'bg-gray-900 text-white hover:bg-gray-800'
                        }
                      `}
                    >
                      <span className="relative z-10">{plan.cta}</span>
                      {isPopular && (
                        <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/30 to-transparent skew-x-12 -translate-x-full group-hover:translate-x-full transition-transform duration-1000"></div>
                      )}
                    </motion.button>
                  </div>

                  {/* Decorative gradient */}
                  {isPopular && (
                    <div className="absolute inset-0 rounded-3xl bg-gradient-to-br from-[#0D7FA3]/5 to-[#C64DAD]/5 pointer-events-none"></div>
                  )}
                </motion.div>
              )
            })}
          </div>

          {/* Bottom note */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={inView ? { opacity: 1 } : {}}
            transition={{ duration: 0.8, delay: 0.8 }}
            className="text-center text-gray-600 mt-12 text-lg"
          >
            ✨ Todos os planos incluem <span className="font-semibold">garantia de 6 meses</span> • Cancele quando quiser
          </motion.p>
        </div>
      </div>
    </section>
  )
}

export default Pricing
